import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import { ArrowRight, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function SharedKit() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/trial/instant-brand/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Brand kit not found"));
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#05050A] text-white">
        <LandingHeader />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-heading text-3xl font-black">Kit not found</h1>
          <p className="text-white/60 mt-3">{err}</p>
          <Link to="/" className="inline-block mt-6 text-cyan-400 underline">Back to home</Link>
        </div>
        <LandingFooter />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#05050A] text-white grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" />
      </div>
    );
  }

  const kit = data.kit || {};
  const brand = kit.brand || {};
  const businessName = data.business_name || "Brand";

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <LandingHeader />
      <main className="max-w-5xl mx-auto px-6 py-12 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-300 text-[11px] uppercase tracking-[0.25em] font-bold">
            <Sparkles className="h-3.5 w-3.5" /> Shared Brand Kit
          </span>
          <h1 className="font-heading mt-5 text-4xl lg:text-6xl font-black tracking-tight">{businessName}</h1>
          <p className="italic text-white/60 mt-3">&ldquo;{kit.tagline}&rdquo;</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Logo */}
          <div className="glass-strong rounded-2xl p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">
              {data.logo_image_b64 ? "AI-Generated Logo" : "Logo Concept"}
            </div>
            {data.logo_image_b64 ? (
              <div className="aspect-square rounded-xl overflow-hidden bg-white">
                <img src={`data:image/png;base64,${data.logo_image_b64}`} alt={businessName} className="w-full h-full object-contain" />
              </div>
            ) : (
              <div
                className="aspect-square rounded-xl flex flex-col items-center justify-center gap-3 border-2"
                style={{
                  background: `linear-gradient(135deg, ${brand.primary_color}25, ${brand.accent_color || "#22d3ee"}15)`,
                  borderColor: `${brand.primary_color}40`,
                }}
              >
                <div className="h-24 w-24 rounded-2xl grid place-items-center shadow-2xl"
                  style={{ background: `linear-gradient(135deg, ${brand.primary_color}, ${brand.accent_color || "#22d3ee"})` }}>
                  <span className="font-heading font-black text-5xl text-white">{businessName.charAt(0).toUpperCase()}</span>
                </div>
                <div className="font-heading font-black text-2xl">{businessName}</div>
              </div>
            )}
          </div>

          {/* Brand details */}
          <div className="space-y-4">
            <div className="glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">Color Palette</div>
              <div className="flex gap-2">
                {[brand.primary_color, brand.accent_color, brand.text_color].filter(Boolean).map((c) => (
                  <div key={c} className="flex-1 text-center">
                    <div className="h-12 rounded-md" style={{ background: c }} />
                    <div className="text-[10px] font-mono uppercase text-white/60 mt-1.5">{c}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">Brand Voice</div>
              <div className="text-sm text-white/80">{brand.voice}</div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">Logo Style</div>
              <div className="text-sm text-white/80">{brand.logo_style}</div>
            </div>
          </div>
        </div>

        {/* Social posts */}
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {[
            ["Facebook", kit.facebook?.body, kit.facebook?.hashtags],
            ["Instagram", kit.instagram?.caption, kit.instagram?.hashtags],
            ["LinkedIn", kit.linkedin?.post, kit.linkedin?.hashtags],
            ["X (Twitter)", kit.x_twitter?.tweet, kit.x_twitter?.hashtags],
            ["Google Business", kit.google_business?.post, []],
          ].map(([name, body, tags]) => body && (
            <div key={name} className="glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">{name}</div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{body}</p>
              {(tags || []).length > 0 && (
                <div className="mt-2 text-[#3b82f6] text-xs">
                  {(tags || []).map((h) => `#${String(h).replace(/^#/, "")}`).join(" ")}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 glass-strong rounded-3xl border border-fuchsia-500/30 p-8 lg:p-10 text-center bg-gradient-to-br from-fuchsia-500/10 via-violet-600/10 to-cyan-500/10">
          <h2 className="font-heading text-2xl lg:text-3xl font-black">Like what you see?</h2>
          <p className="text-white/60 mt-2 max-w-xl mx-auto">
            This is just a sneak peek. Sign up to MakeYourBrand.IN to unlock daily AI content, full website, ad campaigns and more.
          </p>
          <Link
            to={`/register?ref=share&kit_id=${id}&utm_source=share&utm_medium=public_kit&utm_campaign=shared_brand`}
            className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-base text-white bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:shadow-2xl hover:shadow-fuchsia-500/40 transition"
            data-testid="shared-kit-cta"
          >
            Build My Brand <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
